import { useToast } from '@chakra-ui/react';
import {
  ExternalDarwinPropertiesDocument,
  useExternalDarwinPropertiesQuery,
  useExternalDarwinPropertyLazyQuery,
  useExternalGetDarwinPropertyClassificationLazyQuery,
  useExternalGetDarwinSyncPreviewLazyQuery,
  useExternalGetMlsListingSelectionLazyQuery,
  useExternalSyncDarwinFeesMutation,
  useExternalSyncDarwinVendorsMutation,
} from '@client/graphql/__generated__/document-operations';
import { useExternalSyncKeyTermsMutation } from '@client/graphql/__generated__/main-operations';
import {
  DarwinFeeType,
  DarwinPropertyDetailField,
  DarwinPropertyDetailFieldLabel,
  DarwinSyncFeeField,
  DarwinSyncPersonField,
  DarwinSyncPreviewSectionType,
  DarwinVendorType,
  ExternalDarwinPropertiesQuery,
  ExternalDarwinPropertyQuery,
  ExternalGetDarwinSyncPreviewQuery,
  ExternalGetMlsListingSelectionQuery,
  ExternalIngestionFolderFragment,
  ExternalSyncKeyTermsInput,
  ExternalSystem,
  ParticipantRole,
} from '@client/graphql/__generated__/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FeatureKey,
  useIsFeatureEnabledForPayload,
} from '~/apps/consumer/hooks/useIsFeatureEnabledForPayload';
import { MUTE_ERROR_TOAST_QUERY_OPTIONS } from '~/services/document/utils/apollo-client';

/**
 * Keys for Darwin property detail fields that can be retrieved.
 */
export type DarwinPropertyDetailKey =
  | 'typeCodeName'
  | 'propertyTypeName'
  | 'classificationName';

/**
 * Values for Darwin property details, where each field may be a string or null.
 */
export type DarwinPropertyDetailValues = Partial<
  Record<DarwinPropertyDetailKey, string | null>
>;

/**
 * Selection of Darwin property detail fields to sync.
 */
export type DarwinPropertyDetailSelection = DarwinPropertyDetailField[];

export interface PropertyOption {
  value: string;
  label: string;
}

/**
 * Status sync preview field showing current vs proposed values.
 */
export type DarwinStatusSyncPreview =
  ExternalGetDarwinSyncPreviewQuery['externalGetDarwinSyncPreview']['sections'][number]['fields'][number];

/**
 * Vendors sync preview section showing lender and title company fields.
 */
export type DarwinVendorsSyncPreview =
  ExternalGetDarwinSyncPreviewQuery['externalGetDarwinSyncPreview']['vendorsSection'];

/**
 * Fees sync preview section showing transaction management fee and other fees.
 */
export type DarwinFeesSyncPreview =
  ExternalGetDarwinSyncPreviewQuery['externalGetDarwinSyncPreview']['feesSection'];

/**
 * Participants section showing lead source info for buyers/sellers.
 */
export type DarwinParticipantsSyncPreview =
  ExternalGetDarwinSyncPreviewQuery['externalGetDarwinSyncPreview']['participantsSection'];

/** Input for a single fee to sync */
export interface DarwinFeeSyncInput {
  feeType: DarwinFeeType;
  previewData: DarwinSyncFeeField;
}

/**
 * MLS listing selection result from the selection query.
 */
export type MlsListingSelectionResult =
  ExternalGetMlsListingSelectionQuery['externalGetMLSListingSelection'];

/**
 * A ranked MLS listing from the selection query.
 */
export type RankedMlsListing =
  MlsListingSelectionResult['rankedListings'][number];

interface UseDarwinSyncOptions {
  folder?: ExternalIngestionFolderFragment | null;
  selectedOrganizationId?: string;
  variant: 'external' | 'internal';
}

/** User's vendor selection overrides (keyed by vendor type) */
export type VendorSelections = Partial<Record<DarwinVendorType, number>>;

interface UseDarwinSyncReturn {
  isDarwinSyncEnabled: boolean;
  darwinProperties: DarwinPropertyWithParticipants[] | undefined;
  darwinPropertyLoading: boolean;
  darwinSyncLoading: boolean;
  selectedProperty: PropertyOption | null;
  setSelectedProperty: (property: PropertyOption | null) => void;
  propertyOptions: PropertyOption[];
  propertyClassification: DarwinPropertyDetailValues | null;
  propertyClassificationLoading: boolean;
  statusSyncPreview: DarwinStatusSyncPreview | null;
  statusSyncPreviewLoading: boolean;
  vendorsSyncPreview: DarwinVendorsSyncPreview | null;
  vendorsSyncPreviewLoading: boolean;
  vendorSelections: VendorSelections;
  setVendorSelection: (vendorType: DarwinVendorType, personId: number) => void;
  handleDarwinSync: (
    selectedFields?: string[],
    selectedParticipants?: ParticipantRole[],
    selectedPropertyId?: string | null,
    selectedPropertyDetails?: DarwinPropertyDetailSelection,
    syncStatus?: boolean
  ) => Promise<string | null>;
  handleVendorSync: (
    selectedVendorFields: string[],
    selectedPropertyId: string
  ) => Promise<void>;
  vendorSyncLoading: boolean;
  feesSyncPreview: DarwinFeesSyncPreview | null;
  feesSyncPreviewLoading: boolean;
  handleFeeSync: (
    selectedFees: DarwinFeeSyncInput[],
    selectedPropertyId: string
  ) => Promise<void>;
  feeSyncLoading: boolean;
  /** Participants section with lead source info */
  participantsSyncPreview: DarwinParticipantsSyncPreview | null;
  participantsSyncPreviewLoading: boolean;
  /** MLS listing selection result with ranked listings for creating a new Darwin property */
  mlsListingForNewProperty: MlsListingSelectionResult | null;
  mlsListingForNewPropertyLoading: boolean;
  /** Currently selected MLS listing ID (MLSListingObject.id) for creating a new Darwin property */
  selectedMlsListingIdForNewProperty: string | null;
  /** Set the selected MLS listing for creating a new Darwin property */
  setSelectedMlsListingIdForNewProperty: (listingId: string | null) => void;
  /** Refetch MLS listing selection for creating a new Darwin property */
  refetchMlsListingForNewProperty: () => void;
}

type BaseDarwinProperty =
  ExternalDarwinPropertiesQuery['externalDarwinProperties']['properties'][number];

type DarwinPropertyParticipants =
  ExternalDarwinPropertyQuery['externalDarwinProperties']['properties'][number]['participants'];

export type DarwinPropertyWithParticipants = BaseDarwinProperty & {
  participants?: DarwinPropertyParticipants;
};

export const useDarwinSync = ({
  folder,
  selectedOrganizationId,
  variant,
}: UseDarwinSyncOptions): UseDarwinSyncReturn => {
  const toast = useToast();
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyOption | null>(null);

  // Track selected MLS listing ID for creating a new Darwin property
  // Defined early because it's used in isDarwinSyncEnabled computation
  const [
    selectedMlsListingIdForNewProperty,
    setSelectedMlsListingIdForNewProperty,
  ] = useState<string | null>(null);

  // Reset MLS listing selection when folder changes
  useEffect(() => {
    setSelectedMlsListingIdForNewProperty(null);
  }, [folder?.id]);

  const externalFolderId = folder?.folderId;

  // For internal variant, use the folder's organization instead of the selected organization
  const organizationIdForFeatureFlag =
    variant === 'internal' ? folder?.organizationUuid : selectedOrganizationId;

  const isSyncKeyTermsToDarwinFlagEnabled = useIsFeatureEnabledForPayload({
    featureFlagKey: FeatureKey.SYNC_KEY_TERMS_TO_DARWIN,
    organizationId: organizationIdForFeatureFlag,
  });

  // Check if basic prerequisites are met to attempt Darwin sync
  // (feature flag + organization match, but NOT MLS listing - that comes from the selection query)
  const canAttemptDarwinSync = Boolean(
    isSyncKeyTermsToDarwinFlagEnabled &&
      folder?.organizationUuid &&
      (variant === 'internal' ||
        (selectedOrganizationId &&
          folder?.organizationUuid === selectedOrganizationId))
  );

  // Fetch MLS listing selection for the folder - moved up so we can use the result in isDarwinSyncEnabled
  const [
    fetchMlsListingSelection,
    { data: mlsListingSelectionData, loading: mlsListingSelectionLoading },
  ] = useExternalGetMlsListingSelectionLazyQuery({
    fetchPolicy: 'network-only',
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  // Darwin sync is enabled when prerequisites are met AND we have a viable MLS listing
  // Either from the selection query's recommended listing, or from user's explicit selection
  const hasListingForSync = Boolean(
    mlsListingSelectionData?.externalGetMLSListingSelection?.selectedListing
      ?.listingId || selectedMlsListingIdForNewProperty
  );
  const isDarwinSyncEnabled = Boolean(
    canAttemptDarwinSync && !mlsListingSelectionLoading && hasListingForSync
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const darwinSyncUnavailableReason = useMemo(() => {
    if (!isSyncKeyTermsToDarwinFlagEnabled) {
      return null;
    }

    if (!folder) {
      return null;
    }

    if (variant === 'external') {
      if (!selectedOrganizationId) {
        return null;
      }

      if (folder.organizationUuid !== selectedOrganizationId) {
        return 'The selected organization does not match this transaction.';
      }
    }

    // Use the selection query result OR user's explicit selection to determine if we have a viable MLS listing
    const hasSelectedListing =
      mlsListingSelectionData?.externalGetMLSListingSelection?.selectedListing
        ?.listingId || selectedMlsListingIdForNewProperty;

    if (!hasSelectedListing) {
      return (
        mlsListingSelectionData?.externalGetMLSListingSelection
          ?.selectionReason ??
        'Could not find an MLS listing for this transaction.'
      );
    }

    return null;
  }, [
    folder,
    isSyncKeyTermsToDarwinFlagEnabled,
    mlsListingSelectionData,
    selectedMlsListingIdForNewProperty,
    selectedOrganizationId,
    variant,
  ]);

  const { data: allDarwinPropertyData, loading: allDarwinPropertyLoading } =
    useExternalDarwinPropertiesQuery({
      variables: {
        input: {
          externalFolderId,
        },
      },
      skip: !isDarwinSyncEnabled || !externalFolderId,
      ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
    });

  const [
    fetchSelectedDarwinProperty,
    {
      data: selectedDarwinPropertyData,
      loading: selectedDarwinPropertyLoading,
    },
  ] = useExternalDarwinPropertyLazyQuery({
    fetchPolicy: 'network-only',
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  const [
    fetchPropertyClassification,
    {
      data: propertyClassificationData,
      loading: propertyClassificationLoading,
    },
  ] = useExternalGetDarwinPropertyClassificationLazyQuery({
    fetchPolicy: 'network-only',
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  const [
    fetchSyncPreview,
    { data: syncPreviewData, loading: syncPreviewLoading },
  ] = useExternalGetDarwinSyncPreviewLazyQuery({
    fetchPolicy: 'network-only',
    ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
  });

  const [syncKeyTerms, { loading: syncKeyTermsLoading }] =
    useExternalSyncKeyTermsMutation();

  const [syncDarwinVendors, { loading: vendorSyncLoading }] =
    useExternalSyncDarwinVendorsMutation();

  const [syncDarwinFees, { loading: feeSyncLoading }] =
    useExternalSyncDarwinFeesMutation();

  // Track user's vendor selection overrides (defaults to best match from preview)
  const [vendorSelections, setVendorSelections] = useState<VendorSelections>(
    {}
  );

  const setVendorSelection = useCallback(
    (vendorType: DarwinVendorType, personId: number) => {
      setVendorSelections((prev) => ({
        ...prev,
        [vendorType]: personId,
      }));
    },
    []
  );

  useEffect(() => {
    if (!isDarwinSyncEnabled || !selectedProperty?.value || !externalFolderId) {
      return;
    }

    const propertyId = parseInt(selectedProperty.value, 10);

    if (Number.isNaN(propertyId)) {
      return;
    }

    void fetchSelectedDarwinProperty({
      variables: {
        input: {
          externalFolderId,
          propertyId,
        },
      },
    });
  }, [
    externalFolderId,
    fetchSelectedDarwinProperty,
    isDarwinSyncEnabled,
    selectedProperty?.value,
  ]);

  // Fetch property classification for selected property
  useEffect(() => {
    if (
      !isDarwinSyncEnabled ||
      !selectedProperty?.value ||
      !externalFolderId ||
      !folder?.id
    ) {
      return;
    }

    const propertyId = parseInt(selectedProperty.value, 10);

    if (Number.isNaN(propertyId)) {
      return;
    }

    void fetchPropertyClassification({
      variables: {
        input: {
          ingestionFolderId: folder.id,
          propertyId,
        },
      },
    });
  }, [
    externalFolderId,
    folder?.id,
    fetchPropertyClassification,
    isDarwinSyncEnabled,
    selectedProperty?.value,
  ]);

  // Fetch sync preview for selected property (or without propertyId for new property creation)
  useEffect(() => {
    if (!isDarwinSyncEnabled || !externalFolderId || !folder?.id) {
      return;
    }

    const propertyId = selectedProperty?.value
      ? parseInt(selectedProperty.value, 10)
      : undefined;

    if (selectedProperty?.value && Number.isNaN(propertyId)) {
      return;
    }

    void fetchSyncPreview({
      variables: {
        input: {
          ingestionFolderId: folder.id,
          propertyId,
        },
      },
    });
  }, [
    externalFolderId,
    folder?.id,
    fetchSyncPreview,
    isDarwinSyncEnabled,
    selectedProperty?.value,
  ]);

  // Fetch MLS listing selection for the folder - uses canAttemptDarwinSync to allow
  // the query to run even before we have confirmed a viable listing exists
  useEffect(() => {
    if (!canAttemptDarwinSync || !folder?.id) {
      return;
    }

    void fetchMlsListingSelection({
      variables: {
        input: {
          ingestionFolderId: folder.id,
        },
      },
    });
  }, [folder?.id, fetchMlsListingSelection, canAttemptDarwinSync]);

  // Set default MLS listing selection when data is loaded for new property creation
  useEffect(() => {
    const selection = mlsListingSelectionData?.externalGetMLSListingSelection;

    // Clear stale selection when no data is available
    if (!selection) {
      setSelectedMlsListingIdForNewProperty(null);

      return;
    }

    // Auto-select the recommended listing if not already selected
    if (!selectedMlsListingIdForNewProperty && selection.selectedListing?.id) {
      setSelectedMlsListingIdForNewProperty(selection.selectedListing.id);
    }
  }, [mlsListingSelectionData, selectedMlsListingIdForNewProperty]);

  // Refetch MLS listing selection for new property creation
  const refetchMlsListingForNewProperty = useCallback(() => {
    if (!folder?.id) {
      return;
    }

    void fetchMlsListingSelection({
      variables: {
        input: {
          ingestionFolderId: folder.id,
        },
      },
    });
  }, [folder?.id, fetchMlsListingSelection]);

  // Memoize the MLS listing selection result for new property creation
  const mlsListingForNewProperty =
    useMemo<MlsListingSelectionResult | null>(() => {
      return mlsListingSelectionData?.externalGetMLSListingSelection ?? null;
    }, [mlsListingSelectionData]);

  const baseDarwinProperties = useMemo<
    DarwinPropertyWithParticipants[] | undefined
  >(() => {
    const properties =
      allDarwinPropertyData?.externalDarwinProperties?.properties;
    if (properties === undefined) {
      return undefined;
    }

    return properties;
  }, [allDarwinPropertyData?.externalDarwinProperties?.properties]);

  const selectedDarwinPropertyRecord = useMemo<
    DarwinPropertyWithParticipants | undefined
  >(() => {
    if (!selectedProperty?.value) {
      return undefined;
    }

    const property =
      selectedDarwinPropertyData?.externalDarwinProperties?.properties?.[0];

    if (!property) {
      return undefined;
    }

    const selectedPropertyId = parseInt(selectedProperty.value, 10);

    if (Number.isNaN(selectedPropertyId)) {
      return undefined;
    }

    if (property.propertyId !== selectedPropertyId) {
      return undefined;
    }

    return property;
  }, [
    selectedDarwinPropertyData?.externalDarwinProperties?.properties,
    selectedProperty?.value,
  ]);

  const darwinProperties = useMemo<
    DarwinPropertyWithParticipants[] | undefined
  >(() => {
    if (!baseDarwinProperties) {
      return undefined;
    }

    if (!selectedDarwinPropertyRecord) {
      return baseDarwinProperties;
    }

    const propertyExists = baseDarwinProperties.some(
      (property) =>
        property.propertyId === selectedDarwinPropertyRecord.propertyId
    );

    if (!propertyExists) {
      return [...baseDarwinProperties, selectedDarwinPropertyRecord];
    }

    return baseDarwinProperties.map((property) =>
      property.propertyId === selectedDarwinPropertyRecord.propertyId
        ? selectedDarwinPropertyRecord
        : property
    );
  }, [baseDarwinProperties, selectedDarwinPropertyRecord]);

  // Property options for selector
  const propertyOptions: PropertyOption[] = useMemo(() => {
    const options: PropertyOption[] = [];

    // Add all matching Darwin properties
    darwinProperties?.forEach((property) => {
      options.push({
        value: property.propertyId.toString(),
        label: property.propertyAddress
          ? `${property.propertyAddress} (PID: ${property.propertyId})`
          : `PID: ${property.propertyId}`,
      });
    });

    return options;
  }, [darwinProperties]);

  // Compute property classification from query result
  const propertyClassification =
    useMemo<DarwinPropertyDetailValues | null>(() => {
      const classification =
        propertyClassificationData?.externalGetDarwinPropertyClassification;

      if (!classification) {
        return null;
      }

      return {
        typeCodeName: classification.typeCodeName ?? null,
        propertyTypeName: classification.propertyTypeName ?? null,
        classificationName: classification.classificationName ?? null,
      };
    }, [propertyClassificationData]);

  // Extract status field from PROPERTY_DETAILS section
  const statusSyncPreview = useMemo<DarwinStatusSyncPreview | null>(() => {
    const sections = syncPreviewData?.externalGetDarwinSyncPreview?.sections;
    if (!sections) {
      return null;
    }

    const propertyDetailsSection = sections.find(
      (section) =>
        section.sectionType === DarwinSyncPreviewSectionType.PROPERTY_DETAILS
    );

    if (!propertyDetailsSection) {
      return null;
    }

    // Find the Status field within the section
    return (
      propertyDetailsSection.fields.find(
        (field) => field.label === DarwinPropertyDetailFieldLabel.STATUS
      ) ?? null
    );
  }, [syncPreviewData]);

  // Extract vendors section (lender and title company)
  const vendorsSyncPreview = useMemo<DarwinVendorsSyncPreview | null>(() => {
    return (
      syncPreviewData?.externalGetDarwinSyncPreview?.vendorsSection ?? null
    );
  }, [syncPreviewData]);

  // Extract fees section (transaction management fee and future fees)
  const feesSyncPreview = useMemo<DarwinFeesSyncPreview | null>(() => {
    return syncPreviewData?.externalGetDarwinSyncPreview?.feesSection ?? null;
  }, [syncPreviewData]);

  // Extract participants section (lead source info)
  const participantsSyncPreview =
    useMemo<DarwinParticipantsSyncPreview | null>(() => {
      return (
        syncPreviewData?.externalGetDarwinSyncPreview?.participantsSection ??
        null
      );
    }, [syncPreviewData]);

  const darwinPropertyLoading =
    allDarwinPropertyLoading || selectedDarwinPropertyLoading;

  // Set default selected property when properties first load
  useEffect(() => {
    if (
      propertyOptions.length > 0 &&
      !selectedProperty &&
      !darwinPropertyLoading
    ) {
      setSelectedProperty(propertyOptions[0]);
    }
  }, [propertyOptions, selectedProperty, darwinPropertyLoading]);

  const notifyDarwinSyncUnavailable = useCallback(() => {
    toast({
      title: 'Darwin sync unavailable',
      description:
        darwinSyncUnavailableReason ??
        'Darwin sync is not available for this transaction.',
      status: 'info',
      duration: 4000,
      isClosable: true,
    });
  }, [darwinSyncUnavailableReason, toast]);

  const isDarwinSyncAvailable = useCallback(() => {
    if (isDarwinSyncEnabled) {
      return true;
    }

    notifyDarwinSyncUnavailable();

    return false;
  }, [isDarwinSyncEnabled, notifyDarwinSyncUnavailable]);

  const handleDarwinSync = useCallback(
    async (
      selectedFields?: string[],
      selectedParticipants?: ParticipantRole[],
      selectedPropertyId?: string,
      selectedPropertyDetails?: DarwinPropertyDetailSelection | null,
      syncStatus?: boolean
    ) => {
      if (!isDarwinSyncAvailable()) {
        return null;
      }

      // Handle property details selection:
      // - undefined: not specified, don't include in sync input
      // - null: explicitly clear selection, send empty array
      // - array: sync the specified fields
      let propertyDetailsSelection: DarwinPropertyDetailSelection | undefined;

      if (selectedPropertyDetails === null) {
        propertyDetailsSelection = [];
      } else if (Array.isArray(selectedPropertyDetails)) {
        propertyDetailsSelection = selectedPropertyDetails.filter(Boolean);
      }

      const hasSelectedPropertyDetails =
        (propertyDetailsSelection?.length ?? 0) > 0;
      const hasAnySelections =
        Boolean(selectedFields?.length) ||
        Boolean(selectedParticipants?.length) ||
        hasSelectedPropertyDetails ||
        typeof syncStatus === 'boolean';

      if (!externalFolderId || !hasAnySelections) {
        return null;
      }

      try {
        const input: ExternalSyncKeyTermsInput = {
          externalFolderId,
          toSystem: ExternalSystem.DARWIN,
          externalSystemIdentifier: selectedPropertyId,
          onlyKeyTerms: {
            keyTerms: (selectedFields || []).map((term) => ({ term })),
          },
          // Include MLS listing override for creating new Darwin properties
          // This ensures the correct MLS listing data is used
          ...(selectedMlsListingIdForNewProperty && !selectedPropertyId
            ? { mlsListingIdOverride: selectedMlsListingIdForNewProperty }
            : {}),
        };

        if (selectedParticipants?.length) {
          input.onlyParticipants = selectedParticipants.map((role) => ({
            role,
          }));
        }

        // Include environment options for property details and status sync
        if (
          propertyDetailsSelection !== undefined ||
          syncStatus !== undefined
        ) {
          input.environment = {
            darwin: {
              ...(propertyDetailsSelection !== undefined
                ? { propertyDetailsToSync: propertyDetailsSelection }
                : {}),
              ...(syncStatus !== undefined ? { syncStatus } : {}),
            },
          };
        }

        const response = await syncKeyTerms({
          variables: {
            input,
          },
          refetchQueries: [
            {
              query: ExternalDarwinPropertiesDocument,
              variables: {
                input: {
                  externalFolderId,
                },
              },
            },
          ],
          awaitRefetchQueries: true,
          ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
        });

        // Get the external entity ID from the response (e.g., newly created Darwin property ID)
        const externalEntityId =
          response.data?.externalSyncKeyTerms?.externalEntityId ?? null;

        const propertyIdToRefresh =
          externalEntityId ??
          selectedPropertyId ??
          selectedProperty?.value ??
          null;

        if (propertyIdToRefresh) {
          const parsedPropertyId = parseInt(propertyIdToRefresh, 10);

          if (!Number.isNaN(parsedPropertyId)) {
            await fetchSelectedDarwinProperty({
              variables: {
                input: {
                  externalFolderId,
                  propertyId: parsedPropertyId,
                },
              },
            });

            // Refetch sync preview and property classification to reflect updated state
            if (folder?.id) {
              void fetchSyncPreview({
                variables: {
                  input: {
                    ingestionFolderId: folder.id,
                    propertyId: parsedPropertyId,
                  },
                },
              });

              void fetchPropertyClassification({
                variables: {
                  input: {
                    ingestionFolderId: folder.id,
                    propertyId: parsedPropertyId,
                  },
                },
              });
            }
          }
        } else if (folder?.id) {
          // Creating new property - refetch sync preview without propertyId
          void fetchSyncPreview({
            variables: {
              input: {
                ingestionFolderId: folder.id,
              },
            },
          });
        }

        toast({
          title: 'Synced to Darwin',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        return externalEntityId;
      } catch (error) {
        const description =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred.';

        toast({
          title: 'Failed to sync to Darwin',
          status: 'error',
          description,
          duration: 5000,
          isClosable: true,
        });

        return null;
      }
    },
    [
      externalFolderId,
      fetchPropertyClassification,
      fetchSelectedDarwinProperty,
      fetchSyncPreview,
      folder?.id,
      isDarwinSyncAvailable,
      selectedMlsListingIdForNewProperty,
      selectedProperty?.value,
      syncKeyTerms,
      toast,
    ]
  );

  /**
   * Syncs selected vendors to Darwin using the new vendor-specific mutation.
   * Uses pre-computed preview data and user selections to avoid re-computation.
   */
  const handleVendorSync = useCallback(
    async (selectedVendorFields: string[], selectedPropertyId: string) => {
      if (!isDarwinSyncAvailable()) {
        return;
      }

      if (!folder?.id || !vendorsSyncPreview?.fields) {
        return;
      }

      const propertyId = parseInt(selectedPropertyId, 10);
      if (Number.isNaN(propertyId)) {
        return;
      }

      try {
        // Build vendor sync input from selected fields and user selections
        const vendors = vendorsSyncPreview.fields
          .filter((field) => selectedVendorFields.includes(field.label))
          .map((field) => {
            const { vendorType } = field;
            if (!vendorType) {
              throw new Error(`Missing vendorType for field: ${field.label}`);
            }

            // Use user's selection if available, otherwise use best match from preview
            const selectedPersonId =
              vendorSelections[vendorType] ??
              field.proposedValue.matchedPersonId;

            if (!selectedPersonId) {
              throw new Error(
                `No person selected for ${field.label} and no best match available`
              );
            }

            // Include full preview data for audit trail
            return {
              vendorType,
              selectedPersonId,
              previewData: field as DarwinSyncPersonField,
            };
          });

        if (vendors.length === 0) {
          return;
        }

        await syncDarwinVendors({
          variables: {
            input: {
              ingestionFolderId: folder.id,
              darwinPropertyId: propertyId,
              vendors,
              previewTimestamp: new Date(),
            },
          },
          ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
        });

        // Refetch sync preview to reflect updated state
        if (folder.id) {
          void fetchSyncPreview({
            variables: {
              input: {
                ingestionFolderId: folder.id,
                propertyId,
              },
            },
          });
        }

        toast({
          title: 'Vendors synced to Darwin',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (error) {
        const description =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred.';

        toast({
          title: 'Failed to sync vendors to Darwin',
          status: 'error',
          description,
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [
      fetchSyncPreview,
      folder?.id,
      isDarwinSyncAvailable,
      syncDarwinVendors,
      toast,
      vendorSelections,
      vendorsSyncPreview?.fields,
    ]
  );

  /**
   * Syncs selected fees to Darwin using the fee-specific mutation.
   * Uses pre-computed preview data to avoid re-computation.
   */
  const handleFeeSync = useCallback(
    async (selectedFees: DarwinFeeSyncInput[], selectedPropertyId: string) => {
      if (!isDarwinSyncAvailable()) {
        return;
      }

      if (!folder?.id || selectedFees.length === 0) {
        return;
      }

      const propertyId = parseInt(selectedPropertyId, 10);
      if (Number.isNaN(propertyId)) {
        return;
      }

      try {
        await syncDarwinFees({
          variables: {
            input: {
              ingestionFolderId: folder.id,
              darwinPropertyId: propertyId,
              fees: selectedFees.map((fee) => ({
                feeType: fee.feeType,
                previewData: fee.previewData,
              })),
              previewTimestamp: new Date(),
            },
          },
          ...MUTE_ERROR_TOAST_QUERY_OPTIONS,
        });

        // Refetch sync preview to reflect updated state
        if (folder.id) {
          void fetchSyncPreview({
            variables: {
              input: {
                ingestionFolderId: folder.id,
                propertyId,
              },
            },
          });
        }

        toast({
          title: 'Fees synced to Darwin',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (error) {
        const description =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred.';

        toast({
          title: 'Failed to sync fees to Darwin',
          status: 'error',
          description,
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [fetchSyncPreview, folder?.id, isDarwinSyncAvailable, syncDarwinFees, toast]
  );

  // Early return if basic prerequisites are not met (feature flag, organization match)
  // This is different from isDarwinSyncEnabled which also requires a viable MLS listing
  if (!canAttemptDarwinSync) {
    return {
      isDarwinSyncEnabled: false,
      darwinProperties: [],
      darwinPropertyLoading: false,
      darwinSyncLoading: false,
      selectedProperty: null,
      setSelectedProperty: () => {},
      propertyOptions: [],
      propertyClassification: null,
      propertyClassificationLoading: false,
      statusSyncPreview: null,
      statusSyncPreviewLoading: false,
      vendorsSyncPreview: null,
      vendorsSyncPreviewLoading: false,
      vendorSelections: {},
      setVendorSelection: () => {},
      handleDarwinSync: () => Promise.resolve(null),
      handleVendorSync: () => Promise.resolve(),
      vendorSyncLoading: false,
      feesSyncPreview: null,
      feesSyncPreviewLoading: false,
      handleFeeSync: () => Promise.resolve(),
      feeSyncLoading: false,
      participantsSyncPreview: null,
      participantsSyncPreviewLoading: false,
      mlsListingForNewProperty: null,
      mlsListingForNewPropertyLoading: false,
      selectedMlsListingIdForNewProperty: null,
      setSelectedMlsListingIdForNewProperty: () => {},
      refetchMlsListingForNewProperty: () => {},
    };
  }

  return {
    isDarwinSyncEnabled,
    darwinProperties,
    darwinPropertyLoading,
    darwinSyncLoading: syncKeyTermsLoading,
    selectedProperty,
    setSelectedProperty,
    propertyOptions,
    propertyClassification,
    propertyClassificationLoading,
    statusSyncPreview,
    statusSyncPreviewLoading: syncPreviewLoading,
    vendorsSyncPreview,
    vendorsSyncPreviewLoading: syncPreviewLoading,
    vendorSelections,
    setVendorSelection,
    handleDarwinSync,
    handleVendorSync,
    vendorSyncLoading,
    feesSyncPreview,
    feesSyncPreviewLoading: syncPreviewLoading,
    handleFeeSync,
    feeSyncLoading,
    participantsSyncPreview,
    participantsSyncPreviewLoading: syncPreviewLoading,
    mlsListingForNewProperty,
    mlsListingForNewPropertyLoading: mlsListingSelectionLoading,
    selectedMlsListingIdForNewProperty,
    setSelectedMlsListingIdForNewProperty,
    refetchMlsListingForNewProperty,
  };
};
